/*
 * certificate-maker/data.js — the list, the placeholders, and the grammar.
 *
 * THE FEATURE NOBODY ELSE HAS
 * ───────────────────────────
 * A Russian award document does not put the name in the nominative. It says
 * «награждается Иванов Иван» (nominative), but «вручается Иванову Ивану»
 * (dative) and «за заслуги Иванова Ивана» (genitive) — the case is chosen by
 * the sentence, not by the person. A class list, meanwhile, is always in the
 * nominative, because that is how a register is kept.
 *
 * So every batch generator on the market produces grammatically wrong Russian,
 * and the workaround people actually use is to retype thirty names by hand into
 * the right case (this is what the «слияние в Word» articles describe). We have
 * `public/js/fio-engine.js` — petrovich rules, 97.9 % on a 290-cell reference —
 * already in the codebase for /russian-name-declension. Wiring it in here costs
 * one import and removes the single most tedious part of the job.
 *
 * `{fio|дат}` in any field therefore means "this person's name in the dative",
 * and it is resolved per row.
 *
 * ENCODING IS NOT AN AFTERTHOUGHT
 * ───────────────────────────────
 * A list exported from Russian Excel is windows-1251 far more often than UTF-8.
 * `FileReader.readAsText` assumes UTF-8 and hands back mojibake, so imports go
 * through SubtitleParser.decodeBytes, which sniffs by Cyrillic density — the
 * same module /subtitle-editor uses for exactly this reason.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CertData = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* Case aliases people will actually type, mapped to fio-engine's keys. */
    var CASES = {
        'им': 'nomn', 'nom': 'nomn', 'nomn': 'nomn', 'и': 'nomn',
        'род': 'gent', 'gen': 'gent', 'gent': 'gent', 'р': 'gent',
        'дат': 'datv', 'dat': 'datv', 'datv': 'datv', 'д': 'datv',
        'вин': 'accs', 'acc': 'accs', 'accs': 'accs', 'в': 'accs',
        'тв': 'ablt', 'ins': 'ablt', 'ablt': 'ablt', 'т': 'ablt',
        'пр': 'loct', 'loc': 'loct', 'loct': 'loct', 'п': 'loct'
    };

    /* ── list parsing ────────────────────────────────────────────────────── */

    /**
     * Splits pasted text or an imported file into rows of cells. Delimiter is
     * detected rather than configured: a pasted spreadsheet column is tabs, a
     * saved file is semicolons in Russian Excel and commas elsewhere, and a
     * hand-typed list is one name per line with no delimiter at all. Asking the
     * user which one it is fails everybody who does not know.
     */
    function parseList(text, opts) {
        opts = opts || {};
        var lines = String(text || '').replace(/\r\n?/g, '\n').split('\n')
            .filter(function (l) { return l.trim().length; });
        if (!lines.length) return { rows: [], headers: [], delim: null };

        var delim = opts.delim || detectDelim(lines);
        var rows = lines.map(function (l) { return delim ? splitRow(l, delim) : [l.trim()]; });

        /* A header row is claimed only when the FIRST row looks like labels and
         * the rest do not: a list of surnames whose first entry happens to be
         * "Абрамов" must not lose Абрамов to a header. */
        var headers = null;
        if (opts.header === true || (opts.header !== false && looksLikeHeader(rows))) {
            headers = rows.shift().map(function (h) { return String(h).trim(); });
        }
        return { rows: rows, headers: headers, delim: delim };
    }

    function detectDelim(lines) {
        /* ⚠️ THE SAMPLE AND THE THRESHOLD MUST COUNT THE SAME LINES. This
         * counted separators in the first TWELVE lines and compared that total
         * against HALF OF ALL of them, so the longer the list the harder it
         * became to detect anything at all: measured 2026-08-23, a
         * `Имя;Класс;Награда` list split correctly at 30 rows and silently
         * stopped splitting at 50 — every row became one cell and the class
         * column vanished into the name, with nothing said. It hid because
         * lists in testing are short and lists in classrooms are not. */
        var sample = lines.slice(0, 12);
        var probe = sample.join('\n');
        var counts = { '\t': (probe.match(/\t/g) || []).length, ';': (probe.match(/;/g) || []).length, ',': (probe.match(/,/g) || []).length };
        var best = null, bestN = 0;
        for (var k in counts) if (counts[k] > bestN) { bestN = counts[k]; best = k; }
        /* One comma in a list of thirty names is a suffix («Иванов, 5А»), not a
         * column separator, so a delimiter has to appear on most lines. */
        if (!best || bestN < Math.max(1, sample.length * 0.5)) return null;
        return best;
    }

    /* Minimal RFC-4180 quoting: a field may be "quoted, with the delimiter" and
     * doubled quotes inside. Anything more exotic belongs to a CSV library we
     * are not going to ship for a list of names. */
    function splitRow(line, delim) {
        var out = [], cur = '', q = false;
        for (var i = 0; i < line.length; i++) {
            var ch = line[i];
            if (q) {
                if (ch === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; }
                else cur += ch;
            } else if (ch === '"') q = true;
            else if (ch === delim) { out.push(cur.trim()); cur = ''; }
            else cur += ch;
        }
        out.push(cur.trim());
        return out;
    }

    function looksLikeHeader(rows) {
        if (rows.length < 2) return false;
        var h = rows[0];
        var known = /^(фио|имя|фамилия|name|full ?name|класс|class|группа|group|должность|награда|award|место|place|номер|no|№|дата|date|за что|reason|причина|описание|компания|company|организация|email|почта|результат|score|баллы)$/i;
        var hits = h.filter(function (c) { return known.test(String(c).trim()); }).length;
        if (hits >= 1 && hits >= Math.ceil(h.length / 2)) return true;
        /* Or: the first row has no digits and the rest do — a labels row above
         * data. Weak on its own, which is why it needs the length agreement. */
        var firstNoDigits = h.every(function (c) { return !/\d/.test(c); });
        var restHaveDigits = rows.slice(1, 5).some(function (r) { return r.some(function (c) { return /\d/.test(c); }); });
        return firstNoDigits && restHaveDigits && h.length > 1 && hits > 0;
    }

    /* ── the row context ─────────────────────────────────────────────────── */

    /**
     * Turns one parsed row into the dictionary placeholders resolve against:
     * positional keys (1, 2, 3…), header names when there are headers, and the
     * built-ins every document needs.
     */
    function rowContext(row, headers, i, extra) {
        var ctx = {};
        (row || []).forEach(function (cell, k) {
            ctx[String(k + 1)] = cell;
            if (headers && headers[k]) ctx[norm(headers[k])] = cell;
        });
        ctx.n = String(i + 1);
        ctx['№'] = String(i + 1);
        for (var key in extra || {}) if (Object.prototype.hasOwnProperty.call(extra, key)) ctx[norm(key)] = extra[key];
        /* The name is column 1 unless a header claims it. Everything else about
         * this file depends on knowing which cell is a person.
         *
         * ⚠️ EVERY alias has to resolve, not just the canonical one. The first
         * version set only `ctx.fio`, so the RU interface's own `{фио}` — the
         * token the tool inserts for you — resolved to nothing and printed
         * itself, literally, on the certificate (caught on the live page
         * 2026-08-23). A placeholder vocabulary that the tool advertises and
         * then does not honour is worse than having none. */
        var who = ctx.fio || ctx['фио'] || ctx['имя'] || ctx.name || ctx['full name'] || ctx['1'] || '';
        ['fio', 'фио', 'имя', 'name'].forEach(function (k) { if (!ctx[k]) ctx[k] = who; });
        return ctx;
    }

    function norm(s) { return String(s).trim().toLowerCase(); }

    /* ── placeholders ────────────────────────────────────────────────────── */

    var TOKEN = /\{([^{}|]+)(?:\|([^{}]+))?\}/g;

    /* A gender fork: {ученик/ученица}, {награждён/награждена}, {его/её}. Two or
       three slash-separated words with no `|` modifier and no spaces around the
       slash — narrow on purpose, so «{дата/время}» style keys are not eaten. */
    var FORK = /\{([^{}|\/\s]+(?:\/[^{}|\/\s]+){1,2})\}/g;

    /**
     * Resolves gender forks against the recipient's name.
     *
     * @param {string} tpl
     * @param {?string} fio  the recipient, or null when there is none
     * @returns {{text:string, forks:number, guessed:boolean}}
     *          `guessed` = a fork was resolved while the gender was uncertain,
     *          which is exactly what the review table must flag.
     */
    function forks(tpl, fio) {
        var text = String(tpl == null ? '' : tpl);
        if (text.indexOf('/') < 0) return { text: text, forks: 0, guessed: false };
        var v = fio ? nameVerdict(fio) : null;
        var known = !!(v && !v.ambiguous && !v.unknown);
        var female = !!(known && v.gender === 'female');
        var sure = !!(known && (v.gender === 'male' || v.gender === 'female'));
        var n = 0, guessed = false;
        text = text.replace(FORK, function (m, body) {
            var parts = body.split('/');
            n++;
            if (!sure) guessed = true;
            return female ? parts[1] : parts[0];
        });
        return { text: text, forks: n, guessed: guessed };
    }

    /**
     * fill('вручается {фио|дат}', ctx) → 'вручается Иванову Ивану Ивановичу'
     *
     * Unknown keys are left ALONE rather than blanked: a certificate that still
     * says «{класс}» tells the author what went wrong, whereas one with a hole
     * in the sentence gets printed thirty times before anyone notices.
     */
    function fill(tpl, ctx, opts) {
        opts = opts || {};
        var nameKeys = opts.nameKeys || ['fio', 'name'];
        var used = [];
        /* Forks run FIRST, on the template: they choose between words, and the
           word they choose may itself contain nothing else. Running them after
           substitution would let a name that happens to contain a slash be read
           as a fork. */
        var fk = { text: String(tpl == null ? '' : tpl), forks: 0, guessed: false };
        if (opts.gender !== false) {
            var who = null;
            for (var i = 0; i < nameKeys.length && who == null; i++) who = ctx[norm(nameKeys[i])];
            fk = forks(fk.text, who == null ? null : String(who));
        }
        var out = fk.text.replace(TOKEN, function (m, key, mod) {
            var k = norm(key);
            var v = ctx[k];
            if (v == null) {
                used.push({ key: k, ok: false });
                return opts.blankUnknown ? '' : m;
            }
            used.push({ key: k, ok: true });
            /* `{фио}` with no modifier takes the case chosen for the whole batch.
               An explicit `{фио|род}` wins, and it must: otherwise the two would
               compose and a genitive name would be declined a second time — the
               engine would return something that is not any case at all. */
            if (!hasCase(mod) && nameKeys.indexOf(k) >= 0 && opts.defaultCase && opts.defaultCase !== 'nomn') {
                v = decline(String(v), opts.defaultCase, opts);
            }
            return applyMods(String(v), mod, opts);
        });
        return { text: out, tokens: used, forks: fk.forks, forkGuessed: fk.guessed };
    }

    function hasCase(mod) {
        if (!mod) return false;
        return mod.split('|').some(function (m) { return !!CASES[norm(m)]; });
    }

    function applyMods(value, mod, opts) {
        if (!mod) return value;
        mod.split('|').forEach(function (raw) {
            var m = norm(raw);
            if (CASES[m]) { value = decline(value, CASES[m], opts); return; }
            if (m === 'иниц' || m === 'initials') { value = initials(value, opts); return; }
            if (m === 'фам' || m === 'surname') { value = surnameOnly(value, opts); return; }
            if (m === 'верх' || m === 'upper') { value = value.toUpperCase(); return; }
            if (m === 'ниж' || m === 'lower') { value = value.toLowerCase(); return; }
            if (m === 'загл' || m === 'title') { value = titleCase(value); return; }
        });
        return value;
    }

    function titleCase(v) {
        return v.replace(/(^|[\s\-])(\S)/g, function (_, a, b) { return a + b.toUpperCase(); });
    }

    /* ── declension, via the shared engine ───────────────────────────────── */

    var _fioReady = false;
    function fioAvailable() {
        return !!(typeof window !== 'undefined' && window.FioEngine && _fioReady);
    }
    /** Must be awaited before any `|дат` token resolves; falls back silently. */
    function initFio() {
        if (typeof window === 'undefined' || !window.FioEngine) return Promise.resolve(false);
        return Promise.resolve(window.FioEngine.init ? window.FioEngine.init() : null)
            .then(function () { _fioReady = true; return true; })
            .catch(function () { _fioReady = false; return false; });
    }

    /**
     * decline('Иванов Иван Иванович', 'datv') → 'Иванову Ивану Ивановичу'
     *
     * Returns the input unchanged when the engine is unavailable or unsure. A
     * wrong ending on a printed certificate is worse than a nominative one, so
     * "leave it alone" is the correct failure.
     */
    /** Rewrites `grown` so it carries the letter case of `source`.
     *
     * The rule tables are lower case, so an ending appended to «КОВАЛЬЧУК» came
     * out «КОВАЛЬЧУКу». Three cases are worth honouring and no more: ALL CAPS,
     * Capitalised, and everything else left alone — a word with internal caps
     * («О’Коннор», «МакДональд») is already correct because only the tail grew.
     */
    function matchCase(source, grown) {
        if (!source || !grown) return grown;
        var upper = source.toLocaleUpperCase('ru');
        var lower = source.toLocaleLowerCase('ru');
        if (source === upper && source !== lower) return grown.toLocaleUpperCase('ru');
        return grown;
    }

    /** Word-by-word, because a full name can mix cases: «ИВАНОВ Иван». */
    function matchCaseWords(source, grown) {
        var a = String(source).split(/(\s+)/), b = String(grown).split(/(\s+)/);
        if (a.length !== b.length) return matchCase(source, grown);
        for (var i = 0; i < b.length; i++) b[i] = matchCase(a[i], b[i]);
        return b.join('');
    }

    function decline(value, kase, opts) {
        if (!value || kase === 'nomn') return value;
        if (!fioAvailable()) return value;
        try {
            /* The whole line, in the order it was typed: «Полина Иванова» stays
               name-first on the certificate, and a list number or a trailing
               note («, 3А») survives the declension. */
            var line = window.FioEngine.declineLine(value, {
                gender: (opts && opts.gender) || 'auto',
                order: opts && opts.order
            });
            var r = line.result;
            if (!r) return value;
            /* No evidence of gender → no ending. See the header of this file's
               change and the doctrine two lines above. An explicit gender from
               the caller still wins: they know something we do not. */
            if (!(opts && opts.gender && opts.gender !== 'auto') && r.genderSource === 'default') return value;
            var got = line.full[kase];
            /* The rules are lower case; the source may not be. */
            return got && got.trim() ? matchCaseWords(value, got) : value;
        } catch (e) { return value; }
    }

    /** «Иванову И. И.» — the form a document header uses. */
    function initials(value, opts) {
        if (!fioAvailable()) return value;
        try {
            var parts = window.FioEngine.parseFullName(value);
            var r = window.FioEngine.declineFullName(parts, { gender: (opts && opts.gender) || 'auto' });
            var i = r.initials && r.initials.nomn;
            return i && i.after ? i.after : value;
        } catch (e) { return value; }
    }

    function surnameOnly(value) {
        if (!fioAvailable()) return String(value).split(/\s+/)[0] || value;
        try { return window.FioEngine.parseFullName(value).lastname || value; } catch (e) { return value; }
    }

    /** Whether a name declines at all, and how confident we are. */
    function nameVerdict(value) {
        if (!value || !fioAvailable()) return null;
        try {
            var parts = window.FioEngine.parseFullName(value);
            var r = window.FioEngine.declineFullName(parts, { gender: 'auto' });
            return {
                gender: r.gender, genderSource: r.genderSource, ambiguous: !!r.ambiguous,
                /* The engine's real «no idea»: it reports a default rather than
                   raising `ambiguous`, so anything keying off `ambiguous` alone
                   never fired. */
                unknown: r.genderSource === 'default',
                forms: r.full, parts: parts
            };
        } catch (e) { return null; }
    }

    /* ── dates ───────────────────────────────────────────────────────────── */

    /**
     * A Russian document writes «15» мая 2026 г. — the day in guillemets so a
     * pre-printed blank can be filled by hand. `quoted` is that form, and it is
     * the default for RU because it is what the paper actually looks like.
     */
    /**
     * A date is not a number with words around it. The word order, the
     * quotation marks and — in Arabic — the digits themselves belong to the
     * language, so every locale ships its own pattern (`date_formats` in its
     * lang file) and this only fills it in.
     *
     * ⚠️ It used to hardcode the Russian shape and take the «г.» as a suffix
     * argument. The menu labels were translated with care against it and lost:
     * German asked for „25“ Mai and 25. Mai, Korean for 2026년 5월 25일, French
     * for « 25 » with its spaces, Arabic for ٢٥ — and all eight non-Russian
     * languages printed «25» May 2026 on a document meant to be framed. The
     * label a person picks from IS the promise; this is what keeps it (see
     * §11 of certificate-maker-pdf.mjs, which compares the two).
     *
     * Tokens: :d day · :dd padded day · :mm month number · :month month name ·
     * :y year. Longest-first, so :month is consumed before :mm could look at it.
     */
    function formatDate(d, style, months, fmt) {
        if (!(d instanceof Date) || isNaN(d)) return '';
        fmt = fmt || {};
        var day = d.getDate(), mo = d.getMonth(), yr = d.getFullYear();
        /* ISO is ISO in every language — it is the one form that is not the
           locale's to decide. */
        var pat = style === 'iso' ? ':y-:mm-:dd'
            : (fmt[style] || fmt.long || ':d :month :y');
        var out = pat
            .replace(/:month/g, (months && months[mo]) || '')
            .replace(/:dd/g, pad(day))
            .replace(/:mm/g, pad(mo + 1))
            .replace(/:d/g, String(day))
            .replace(/:y/g, String(yr));
        return fmt.digits ? nativeDigits(out, fmt.digits) : out;
    }
    /** '٠١٢٣٤٥٦٧٨٩' → 2026 becomes ٢٠٢٦. Arabic writes its own numerals. */
    function nativeDigits(s, set) {
        return s.replace(/[0-9]/g, function (c) { return set.charAt(+c) || c; });
    }
    function pad(v) { return (v < 10 ? '0' : '') + v; }

    /* ── numbering ───────────────────────────────────────────────────────── */

    function seq(i, o) {
        o = o || {};
        var v = (o.start == null ? 1 : +o.start) + i * (o.step == null ? 1 : +o.step);
        var s = String(Math.abs(v));
        while (s.length < (o.pad || 0)) s = '0' + s;
        return (o.prefix || '') + (v < 0 ? '-' : '') + s + (o.suffix || '');
    }

    /* ── decoding an imported file ───────────────────────────────────────── */

    /**
     * Reads a .csv/.txt/.tsv as TEXT with the encoding sniffed, not assumed.
     * SubtitleParser is the shared module that already does BOM → UTF-16 →
     * strict UTF-8 → cp1251-by-Cyrillic-density → cp1252; duplicating that here
     * would give us a second, worse copy of it.
     */
    function readTextFile(file) {
        return file.arrayBuffer().then(function (buf) {
            if (typeof window !== 'undefined' && window.SubtitleParser && window.SubtitleParser.decodeBytes) {
                return window.SubtitleParser.decodeBytes(new Uint8Array(buf));
            }
            return new TextDecoder('utf-8').decode(buf);
        }).then(function (r) {
            return typeof r === 'string' ? r : (r && r.text) || '';
        });
    }

    return {
        parseList: parseList, rowContext: rowContext, fill: fill,
        decline: decline, initials: initials, nameVerdict: nameVerdict, initFio: initFio,
        forks: forks,
        formatDate: formatDate, seq: seq, readTextFile: readTextFile,
        CASES: CASES, _detectDelim: detectDelim, _splitRow: splitRow, _looksLikeHeader: looksLikeHeader
    };
});
